import ChangeCard from "components/ChangeCard";
import InsightCard from "components/insights/InsightCard";
import CountdownInsights from "components/insights/InsightsOverview";
import { InstagramAccount } from "components/InstagramData";
import React, { useState } from "react";

// Mocked Graph API Response (inline)

function ChevronDown({ className = "w-4 h-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M6 8l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AccountSelector({ accounts, selectedId, onSelect }) {
  const [open, setOpen] = useState(false);
  const selected = accounts.find((a) => a.id === selectedId) || accounts[0];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((s) => !s)}
        className="flex items-center gap-3 px-4 py-2 bg-white rounded-2xl shadow-sm border border-gray-100"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <img
          src={selected.profile_picture_url}
          alt="profile"
          className="w-8 h-8 rounded-full"
        />
        <div className="text-left">
          <div className="font-medium text-sm">{selected.name}</div>
          <div className="text-xs text-gray-500">@{selected.username}</div>
        </div>
        <span className="ml-2">
          <ChevronDown />
        </span>
      </button>

      {open && (
        <ul
          className="absolute mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-100 z-20 overflow-hidden"
          role="listbox"
        >
          {accounts.map((acc) => (
            <li
              key={acc.id}
              className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer"
              role="option"
              aria-selected={acc.id === selectedId}
              onClick={() => {
                onSelect(acc.id);
                setOpen(false);
              }}
            >
              <img
                src={acc.profile_picture_url}
                alt="profile"
                className="w-7 h-7 rounded-full"
              />
              <div>
                <div className="text-sm font-medium">{acc.name}</div>
                <div className="text-xs text-gray-500">@{acc.username}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TopPosts({ posts }) {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-3">Top Performing Posts</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {posts.map((p) => (
          <div
            key={p.id}
            className="bg-white rounded-2xl overflow-hidden shadow-sm"
          >
            <img
              src={p.thumbnail}
              alt={p.caption}
              className="w-full h-48 object-cover"
            />
            <div className="p-3 flex items-center justify-between text-sm text-gray-700">
              <div className="flex flex-col text-xs">
                <span className="font-medium">{p.caption}</span>
              </div>
              <div className="flex gap-3">
                <div className="text-xs">❤️ {p.likes}</div>
                <div className="text-xs">💬 {p.comments}</div>
                <div className="text-xs">🔖 {p.saves}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function InstagramMetrics({ graphAPIData }) {
  const data = (graphAPIData as InstagramAccount[]) ?? [];

  const [selectedAccountId, setSelectedAccountId] = useState<string>(
    data.length > 0 ? data[0].id : ""
  );

  if (data.length === 0) {
    return <p>Missing Data</p>;
  }

  const account = data.find((a) => a.id === selectedAccountId) ?? data[0];

  return (
    <div className="w-full max-w-6xl mx-auto p-6 space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <AccountSelector
            accounts={data}
            selectedId={selectedAccountId}
            onSelect={setSelectedAccountId}
          />
        </div>

        <div className="text-right">
          <div className="text-xs text-gray-500">Last synced</div>
          <span className="text-xs text-gray-500">
            Updated{" "}
            {new Date().toLocaleString(undefined, {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>{" "}
        </div>
      </div>

      <section aria-labelledby="metrics">
        <h2 id="metrics" className="sr-only">
          Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(account.metrics).map(([key, m]) => (
            <ChangeCard
              key={key}
              label={key.charAt(0).toUpperCase() + key.slice(1)}
              metric={m.value}
              change={m.mom.toLocaleString()}
              yoyChange={m.yoy.toLocaleString()}
            />
          ))}
        </div>
      </section>

      <section aria-labelledby="insights">
        <CountdownInsights items={account.insights} showRealInsights={true} />
      </section>

      <section aria-labelledby="top-posts">
        <TopPosts posts={account.topPosts} />
      </section>

      <footer className="text-sm text-gray-500"></footer>
    </div>
  );
}
